/**
 * TypeScript types for the Co-curricular Presets admin surface.
 *
 * Mirrors the Pydantic shapes returned by pymble-apis at /group/presets/ and
 * /group/admin/presets/. Keep in sync with my-pymble-portal/src/types/preset.ts
 * (the portal owns the user-facing surface; this is the admin equivalent
 * extended with the Phase 2 fields - manager_contact_ids/labels, is_manager,
 * the flip-audience request body and the admin create-on-behalf request body).
 *
 * Identity fields (owner_contact_id, manager_contact_ids) are numeric Edumate
 * contact ids; constituent / individual ids are prefixed strings (e.g.
 * "Group/abc", "ContactPersonal/xyz").
 */

export type PresetAudience = "personal" | "shared";

/**
 * Derivation metadata carried by a COMPANION preset (apis `PresetDerivedFrom`).
 *
 * Presence of this field is the single signal that a preset's membership is
 * computed rather than authored: the materialiser re-derives it on every run,
 * so any hand-edited definition would be silently reverted within the hour.
 * The UI must therefore suppress every definition-editing affordance when it
 * is set. `mode` is a one-member union today; keep it a union so a future
 * derivation mode fails type-check rather than rendering as "carers".
 */
export interface PresetDerivedFrom {
  preset_id: string;
  mode: "carers";
}

export interface PresetDefinition {
  constituent_group_ids: string[];
  individual_contact_ids: string[];
  /** Response-only: `{"ContactPersonal/<id>": display name}` map populated by
   *  the API on every CRUD response so the edit picker can render real names
   *  on chips for pre-saved selections without re-searching each one. Keys
   *  that are missing fall back to a generic "Contact" placeholder. Site-admin
   *  callers get every id in the definition resolved (apis#1114); non-admin
   *  callers stay visibility-gated by design, so keep the fallback. Safe to
   *  leave empty on PresetCreateRequest / PresetUpdateRequest - the server
   *  ignores inbound values and re-resolves from individual_contact_ids. */
  individual_contact_labels?: Record<string, string>;
  /** Response-only: `{"Group/<id>": Name.Short}` map populated by the API
   *  (apis#804) so the detail page can render constituent group names instead
   *  of raw ids. Site-admin callers get every constituent resolved
   *  (apis#1114); non-admins stay visibility-gated and missing keys fall back
   *  to a sliced id. Ignored on inbound create/update requests. */
  constituent_group_labels?: Record<string, string>;
}

export interface PresetMaterialisationStatus {
  last_run_at: string | null;
  last_run_status: "success" | "error" | null;
  last_error: string | null;
  last_member_count: number | null;
  /** True when the create-time materialiser hit its 10s timeout and the
   *  hourly Dagster run will finish the work. */
  deferred: boolean;
}

export interface Preset {
  /** Bare uuid that doubles as the materialised Group's `_id`. Use THIS for
   *  the `/presets/{id}` route and the `["preset-detail", id]` query key.
   *  Routing with `group_id` (the prefixed form) would 404. */
  id: string;
  /** Prefixed edge-reference form, `"Group/{id}"`. Used in graph edges, not
   *  as a route param. Prefer `id` for navigation and cache invalidation. */
  group_id: string;
  name_short: string;
  name_long: string;
  audience: PresetAudience;
  owner_contact_id: number;
  /** Response-only owner display name (apis#1138). Visibility-gated by the
   *  API exactly like the label maps below: absent or null means the caller
   *  cannot resolve that contact, so the id alone must render. Ignored on
   *  inbound create/update requests. */
  owner_contact_label?: string | null;
  manager_contact_ids: number[];
  /** Response-only label map for the manager list. Visibility-gated by the
   *  API: missing keys mean the caller cannot see that contact, so the chip
   *  must render a generic "Manager" placeholder rather than the raw id. */
  manager_contact_labels: Record<number, string>;
  /** True iff the caller is in `manager_contact_ids`. Owner / site-admin
   *  callers see `false` here; their access does NOT come from this flag. */
  is_manager: boolean;
  definition: PresetDefinition;
  privacy_mode: string;
  materialisation: PresetMaterialisationStatus;
  /** Suppressed (zero) when materialised Settings.ShowMemberList is False -
   *  see pymble-apis _build_response privacy fix. */
  member_count: number;
  /** The CONFIGURED member-list visibility override (null = no override,
   *  the materialiser computes it). NOT the effective visibility - that
   *  lives on the companion Group's Settings.ShowMemberList. */
  show_member_list?: boolean | null;
  /** Set on a SOURCE preset that has a companion parent group. Bare uuid, so
   *  it routes straight to `/presets/{id}` and keys `usePresetDetail`. */
  companion_preset_id?: string | null;
  /** Set on the COMPANION itself. Non-null means membership is derived - see
   *  {@link PresetDerivedFrom}. Never both this and `companion_preset_id`:
   *  derivation does not chain. */
  derived_from?: PresetDerivedFrom | null;
  created_at: string;
  updated_at: string;
}

/**
 * Companion-creation fields shared by the create and admin-create bodies
 * (apis `PresetCreateRequest`, inherited by `PresetAdminCreateRequest`).
 *
 * ONE request creates both presets server-side - never send a second create
 * call from the client. Omit all three when the operator did not ask for a
 * companion; the server defaults `create_companion_parent_group` to false and
 * an explicit `false` only adds noise to the payload.
 *
 * Both names are capped at the same lengths as the source preset's
 * (`name_short` 80, `name_long` 240), so a "<source> - Parents" default must be
 * truncated rather than sent long - the server answers a 422, not a trim.
 */
export interface PresetCompanionCreateFields {
  create_companion_parent_group?: boolean;
  companion_name_short?: string;
  companion_name_long?: string;
}

/** Max length of `name_short` / `companion_name_short` server-side. */
export const PRESET_NAME_SHORT_MAX = 80;
/** Max length of `name_long` / `companion_name_long` server-side. */
export const PRESET_NAME_LONG_MAX = 240;
/** Suffix the clipboard parent-group sync uses ("<name> - Parents"). */
export const COMPANION_NAME_SUFFIX = " - Parents";

/**
 * Body for `POST /group/admin/presets/{preset_id}/companion` (site-admin
 * only) - adds a companion to a preset that was created without one.
 *
 * Reuses the `companion_name_*` vocabulary the frozen create contract already
 * defines for these exact two values, so one name means one thing across both
 * creation paths. Omit both to let the server apply the "<source> - Parents"
 * default.
 */
export interface PresetCompanionAddRequest {
  companion_name_short?: string;
  companion_name_long?: string;
}

export interface PresetCreateRequest extends PresetCompanionCreateFields {
  name_short: string;
  name_long: string;
  audience?: PresetAudience;
  definition: PresetDefinition;
  /** Optional on create. The API enforces V1: manager_contact_ids requires
   *  `audience="shared"` - sending managers on a personal preset returns a
   *  422 with code `managers_require_shared_audience`. */
  manager_contact_ids?: number[];
  /** Explicit member-list visibility override. Omit to let the materialiser
   *  compute it; the import path sends false. `true` is site-admin only
   *  (403 otherwise). */
  show_member_list?: boolean;
}

export interface PresetUpdateRequest {
  name_short?: string;
  name_long?: string;
  definition?: PresetDefinition;
  /** Owner / site-admin callers may update the manager list; manager-only
   *  callers are blocked by V6 (403 with code
   *  `managers_cannot_edit_managers`). Same V1 rule applies: managers
   *  require shared audience. */
  manager_contact_ids?: number[];
  /** Tri-state on the API: omitted leaves the override unchanged, explicit
   *  null clears it (site-admin only, like `true`). */
  show_member_list?: boolean | null;
}

/**
 * Body for `POST /group/presets/{id}/flip-audience` (site-admin only).
 *
 *   personal -> shared: pass the manager list to attach. Empty list is fine
 *                       (V4 reuses V2 visibility on each id).
 *   shared   -> personal: `manager_contact_ids` MUST be empty - V5 returns a
 *                       422 with code `manager_contact_ids_must_be_empty`
 *                       and `current_manager_count` when managers are
 *                       attached, so the UI must clear them first.
 */
export interface PresetFlipAudienceRequest {
  target_audience: PresetAudience;
  manager_contact_ids: number[];
}

/**
 * Body for `POST /group/admin/presets/` (site-admin create-on-behalf).
 *
 * Extends PresetCreateRequest with an explicit owner. The API forces
 * `audience="shared"` regardless of inbound value and bypasses owner-side
 * visibility on constituents + managers (cross-cutting #3 in the spec). V8
 * validates that `owner_contact_id` is an active ContactPersonal
 * (`EndAt: null`) - the picker should prefer pre-validating before submit.
 */
export interface PresetAdminCreateRequest extends PresetCompanionCreateFields {
  owner_contact_id: number;
  name_short: string;
  name_long: string;
  /** Always serialised as "shared" by the server; included here for clarity. */
  audience?: PresetAudience;
  definition: PresetDefinition;
  manager_contact_ids?: number[];
  /** Explicit member-list visibility override. Omit to let the materialiser
   *  compute it; the import path sends false. */
  show_member_list?: boolean;
}

/**
 * One row of the site-admin membership read (apis#1166, merged as
 * pymble-apis PR #1171 - `PresetMembershipMember`).
 */
export interface PresetMembershipMember {
  /** Prefixed edge reference, `"ContactPersonal/<id>"`, exactly as the
   *  `GroupMember` edge stores it - the same convention
   *  `PresetDefinition.individual_contact_ids` uses. */
  contact_id: string;
  /** Display name. Null when the contact document is missing or carries no
   *  name, so the row must render a placeholder rather than be dropped. */
  label?: string | null;
}

/**
 * Payload of `GET /group/admin/presets/{preset_id}/membership` (apis#1166).
 *
 * The privileged READ that deliberately ignores the member-list gate, so an
 * administrator can check a companion parent group is correct without
 * revealing it to the parents in it. It performs no writes: reading it never
 * changes effective visibility, `ShowMemberList` or `VisibilityRevision` -
 * which is exactly what separates it from the reveal control on the preset
 * page. Site-admin only (403 otherwise), so callers must gate the request on
 * `useMyDetails().hasAdminAccess`.
 *
 * `member_count` here is a LIVE count of current membership edges, NOT the
 * suppressed `Preset.member_count`: this endpoint does not suppress, and the
 * count is guaranteed to agree with `members` because both come from one
 * aggregation. `last_member_count` / `last_run_*` are the materialiser's own
 * (also unsuppressed) view, so a disagreement between the two is visible.
 */
export interface PresetMembershipResponse {
  preset_id: string;
  /** Prefixed `"Group/<id>"` form of the materialised group. */
  group_id: string;
  /** EFFECTIVE member-list visibility (the Group's `Settings.ShowMemberList`),
   *  not the preset-side override `Preset.show_member_list` carries. False is
   *  the normal state for a companion, and is what the preview labels. */
  show_member_list: boolean;
  derived_from?: PresetDerivedFrom | null;
  member_count: number;
  last_member_count?: number | null;
  last_run_at?: string | null;
  last_run_status?: "success" | "error" | null;
  last_error?: string | null;
  members: PresetMembershipMember[];
  limit: number;
  skip: number;
}

/** Query params for `GET /group/admin/presets/{id}/membership`. */
export interface PresetMembershipParams {
  /** 1-100 inclusive (server caps at 100 - every row is a person). */
  limit?: number;
  /** >=0, default 0. */
  skip?: number;
}

/** Server-side maximum (and default) page size of the membership read. */
export const PRESET_MEMBERSHIP_PAGE_MAX = 100;

/** Query params for `GET /group/admin/presets/` (site-admin list). */
export interface AdminPresetListParams {
  audience?: PresetAudience | "all";
  deleted_at?: "include" | "exclude";
  owner_contact_id?: number;
  /** Case-insensitive name contains. Server enforces 120 char cap. */
  search?: string;
  /** 1-500 inclusive, default 100. */
  limit?: number;
  /** >=0, default 0. */
  skip?: number;
}
